// Admin → Work Management → Access & Assignment (Universal Work design §8).
// Tenant policy toggles + the manager assignment scope. Stored by
// work-management-service (work.tenant_work_policy); every capability check
// (create / assign / reassign / collaborate / subtasks) reads it server-side —
// this page only edits the source of truth. Mirrors DelegationPolicyPage.
// Phase 5: assignment groups (Custom group scope) live below the toggles.
// Completion policy (13-completion-policy.md): the Completion card sets the
// company defaults for "finishing needs review", parent auto-close, and whether
// work people assign to THEMSELVES needs a reviewer too.
// Whose work a manager may SEE is the manager WORKLOAD visibility scope on
// the Workload & Health page; the server synchronizes auth's management scope
// from it (`scope_sync` — shown here too, never mirrored from the browser).
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Info, ShieldCheck } from 'lucide-react';
import { RequirePermission } from '@/rbac/RequirePermission';
import { getApiErrorMessage } from '@/lib/apiError';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Switch } from '@/features/shared/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/features/shared/components/ui/radio-group';
import { Label } from '@/features/shared/components/ui/label';
import type { EmployeeAssignmentScope, ManagerAssignmentScope, WorkPolicy } from '@/services/workService';
import { useWorkPolicy } from '@/features/work/api/hooks';
import { AssignmentGroupsSection } from './WorkAssignmentGroups';
import { ScopeSyncBadge } from './WorkloadPolicyPage';

type ToggleKey = Exclude<keyof WorkPolicy, 'employee_assignment_scope' | 'manager_assignment_scope'>;

/** Completion policy (13-completion-policy.md) ships with review and parent
 * auto-close ON, so no company's process changes on upgrade. An older backend
 * omits the fields, and the PUT replaces the whole policy — so read each one as
 * its documented default rather than writing a silent `false` over it. */
export const withCompletionDefaults = (p: WorkPolicy): WorkPolicy => ({
  ...p,
  completion_requires_review: p.completion_requires_review !== false,
  parent_auto_close: p.parent_auto_close !== false,
  // This one ships OFF: nobody may review their own work, so requiring review on
  // self-assigned work is what stranded items with no possible approver.
  self_assigned_needs_review: p.self_assigned_needs_review === true,
});

const WorkAccessPage: React.FC = () => {
  const { t } = useTranslation('work');
  const k = (key: string, dflt: string, opts: Record<string, unknown> = {}) => t(`policy_${key}`, { defaultValue: dflt, ...opts }) as string;
  const { policy: saved, me, scopeSync, loading, error, reload, save, resync } = useWorkPolicy();
  const [draft, setDraft] = useState<WorkPolicy | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (saved) setDraft(withCompletionDefaults(saved)); }, [saved]);
  useEffect(() => {
    if (error) toast.error(getApiErrorMessage(error, k('load_failed', 'Could not load the work policy.')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const set = <K extends keyof WorkPolicy>(key: K, value: WorkPolicy[K]) => setDraft((p) => (p ? { ...p, [key]: value } : p));
  const base = saved ? withCompletionDefaults(saved) : null;
  const dirty = !!draft && !!base && JSON.stringify(draft) !== JSON.stringify(base);

  const submit = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await save(draft);
      toast.success(k('saved', 'Work policy saved.'));
    } catch (e) {
      toast.error(getApiErrorMessage(e, k('save_failed', 'Could not save the work policy.')));
    } finally {
      setSaving(false);
    }
  };

  const toggles: Array<{ key: ToggleKey; label: string; help: string }> = [
    { key: 'work_available_to_all', label: k('available_to_all', 'Work is available to everyone'), help: k('available_to_all_help', 'Off: only people with assign or review capability see the Work area. On: every employee gets My Work, Created by Me and Collaborating.') },
    { key: 'employees_can_create', label: k('employees_can_create', 'Employees can create work'), help: k('employees_can_create_help', 'Lets anyone with work access create their own work items (drafts and self-owned tasks). Managers and coordinators are unaffected.') },
    { key: 'employees_can_assign', label: k('employees_can_assign', 'Employees can assign work to others'), help: k('employees_can_assign_help', 'Grants employees the assign capability within the scope below. Off: only managers, coordinators and admins assign.') },
    { key: 'managers_assign_outside_line', label: k('managers_outside_line', 'Managers can assign outside their reporting line'), help: k('managers_outside_line_help', 'Off: managers assign only to their direct reports. On: the manager assignment scope below applies.') },
    { key: 'allow_cross_department_collab', label: k('cross_dept', 'Allow cross-department collaboration'), help: k('cross_dept_help', 'Collaborators may be added from other departments (within the assigner’s scope). Off: collaborators must share the owner’s department.') },
    { key: 'allow_reassignment', label: k('reassignment', 'Allow reassignment'), help: k('reassignment_help', 'An active work item can be handed to a different owner (with a reason and notification). Off: ownership is fixed once assigned.') },
    { key: 'allow_collaborators', label: k('collaborators', 'Allow collaborators'), help: k('collaborators_help', 'Work items can carry many contributors alongside the one accountable owner. Off: individual work only.') },
    { key: 'allow_cross_team_subtasks', label: k('cross_team_subtasks', 'Allow cross-team sub-items'), help: k('cross_team_subtasks_help', 'Sub-items may be owned by people outside the parent owner’s team, within the assigner’s scope. Off: sub-items stay inside the team.') },
  ];

  // Completion (13-completion-policy.md §6). Review removes a PERSON from the
  // loop, never a requirement — say so plainly, it is what makes this safe.
  const completionToggles: Array<{ key: 'completion_requires_review' | 'parent_auto_close' | 'self_assigned_needs_review'; label: string; help: string }> = [
    {
      key: 'completion_requires_review',
      label: k('completion_requires_review', 'Finishing needs review'),
      help: k('completion_requires_review_help', 'On: when the assignee finishes, they hand the work to a reviewer, who completes it. Off: they complete it themselves. Either way, required checklist items and required completion evidence still apply — review removes a person from the loop, not the requirements. A work type can override this.'),
    },
    {
      key: 'parent_auto_close',
      label: k('parent_auto_close', 'Close a parent automatically when its sub-tasks finish'),
      help: k('parent_auto_close_help', 'The parent moves as far as its own policy allows — completed, or into the reviewer\u2019s queue. It is never closed while it still has unfinished requirements of its own; it says so on its timeline instead and notifies the owner.'),
    },
    {
      key: 'self_assigned_needs_review',
      label: k('self_assigned_needs_review', 'Work people assign to themselves also needs review'),
      help: k('self_assigned_needs_review_help', 'Off by default. Someone cannot review their own work, so leaving this on means every self-assigned item needs a second person who can approve it — and stalls until one does.'),
    },
  ];

  const employeeScopes: Array<{ value: EmployeeAssignmentScope; label: string; help: string }> = [
    { value: 'DEPARTMENT', label: k('emp_scope_department', 'Same department'), help: k('emp_scope_department_help', 'Employees assign to colleagues in their own department.') },
    { value: 'REPORTING_TREE', label: k('emp_scope_tree', 'Same reporting tree'), help: k('emp_scope_tree_help', 'Employees assign within the people who share their manager chain.') },
    { value: 'TENANT', label: k('emp_scope_tenant', 'Entire company'), help: k('emp_scope_tenant_help', 'Employees can assign to anyone in the company.') },
    { value: 'CUSTOM', label: k('emp_scope_custom', 'Custom group'), help: k('emp_scope_custom_help', 'Employees who are assigners in an assignment group assign to that group’s members.') },
  ];
  const managerScopes: Array<{ value: ManagerAssignmentScope; label: string; help: string; soon?: boolean }> = [
    { value: 'DIRECT_REPORTS', label: k('mgr_scope_direct', 'Direct reports'), help: k('mgr_scope_direct_help', 'Only the people who report straight to the manager.') },
    { value: 'REPORTING_TREE', label: k('mgr_scope_tree', 'All reports'), help: k('mgr_scope_tree_help', 'Everyone below the manager in the reporting tree.') },
    { value: 'DEPARTMENT', label: k('mgr_scope_department', 'Department'), help: k('mgr_scope_department_help', 'Everyone in the manager’s department, reporting line or not.') },
    { value: 'BUSINESS_UNIT', label: k('mgr_scope_bu', 'Business unit'), help: k('mgr_scope_bu_help', 'Everyone in the manager’s business unit.'), soon: true },
    { value: 'TENANT', label: k('mgr_scope_tenant', 'Entire company'), help: k('mgr_scope_tenant_help', 'Managers can assign to anyone in the company.') },
    { value: 'CUSTOM', label: k('mgr_scope_custom', 'Custom group'), help: k('mgr_scope_custom_help', 'Managers assign within the assignment groups where they are an assigner (see Assignment groups below).') },
  ];

  return (
    <RequirePermission perm="work:manage">
      <div className="space-y-6 max-w-3xl">
        <div className="flex items-start gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-[12px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300 shrink-0">
            <ShieldCheck className="w-5 h-5" />
          </span>
          <div>
            <h1 className="text-h2 text-charcoal">{k('title', 'Access & Assignment')}</h1>
            <p className="text-sm text-muted-foreground">
              {k('subtitle', 'Who can see, create and assign work, and how far assignment reaches. Visibility of other people’s work is governed separately by management scope.')}
            </p>
            {me && <Badge variant="outline" className="mt-2">{k('your_scope', 'Your assignment scope: {{scope}}', { scope: me.scope_summary || me.assignment_scope })}</Badge>}
            <ScopeSyncBadge scopeSync={scopeSync} onRetry={resync} className="mt-2" />
          </div>
        </div>

        {loading && !draft && <p className="text-sm text-muted-foreground">{t('common.loading', { defaultValue: 'Loading…' })}</p>}
        {!loading && !draft && (
          <div className="rounded-[10px] border border-border p-4 text-sm text-muted-foreground flex items-center justify-between gap-3">
            <span>{k('load_failed', 'Could not load the work policy.')}</span>
            <Button variant="outline" size="sm" onClick={() => reload()}>{k('retry', 'Retry')}</Button>
          </div>
        )}

        {draft && (
          <>
            <section className="rounded-[12px] border border-border divide-y divide-border">
              {toggles.map((row) => {
                const dependsOnCollab = row.key === 'allow_cross_department_collab' && !draft.allow_collaborators;
                return (
                  <div key={row.key} className="flex items-start justify-between gap-4 p-4">
                    <div className="min-w-0">
                      <Label htmlFor={`pol-${row.key}`} className="text-sm font-medium text-charcoal">{row.label}</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">{row.help}</p>
                      {row.key === 'employees_can_assign' && draft.employees_can_assign && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-charcoal mb-1.5">{k('emp_scope', 'Employee assignment scope')}</p>
                          <RadioGroup value={draft.employee_assignment_scope} onValueChange={(v) => set('employee_assignment_scope', v as EmployeeAssignmentScope)}>
                            {employeeScopes.map((o) => (
                              <div key={o.value} className="flex items-start gap-2">
                                <RadioGroupItem id={`emp-scope-${o.value}`} value={o.value} className="mt-0.5" />
                                <Label htmlFor={`emp-scope-${o.value}`} className="text-sm font-normal">
                                  {o.label}
                                  <span className="block text-xs text-muted-foreground">{o.help}</span>
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                        </div>
                      )}
                      {row.key === 'managers_assign_outside_line' && (
                        <div className="mt-3" aria-disabled={!draft.managers_assign_outside_line}>
                          <p className="text-xs font-medium text-charcoal mb-1.5">
                            {k('mgr_scope', 'Manager assignment scope')}
                            {!draft.managers_assign_outside_line && (
                              <span className="ml-2 font-normal text-muted-foreground">{k('mgr_scope_locked', '(turn on the toggle to change)')}</span>
                            )}
                          </p>
                          <RadioGroup value={draft.manager_assignment_scope}
                            disabled={!draft.managers_assign_outside_line}
                            onValueChange={(v) => set('manager_assignment_scope', v as ManagerAssignmentScope)}>
                            {managerScopes.map((o) => (
                              <div key={o.value} className="flex items-start gap-2">
                                <RadioGroupItem id={`mgr-scope-${o.value}`} value={o.value} className="mt-0.5" disabled={o.soon || !draft.managers_assign_outside_line} />
                                <Label htmlFor={`mgr-scope-${o.value}`} className={`text-sm font-normal ${o.soon ? 'text-muted-foreground' : ''}`}>
                                  {o.label}
                                  {o.soon && <Badge variant="outline" className="ml-2 text-[10px]">{k('coming_soon', 'coming soon')}</Badge>}
                                  <span className="block text-xs text-muted-foreground">{o.help}</span>
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                        </div>
                      )}
                    </div>
                    <Switch id={`pol-${row.key}`} checked={!!draft[row.key]} disabled={saving || dependsOnCollab}
                      onCheckedChange={(v) => set(row.key, v)} aria-label={row.label} />
                  </div>
                );
              })}
            </section>

            <section className="rounded-[12px] border border-border" data-testid="completion-card">
              <div className="px-4 pt-4">
                <h2 className="text-sm font-semibold text-charcoal">{k('completion', 'Completion')}</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {k('completion_intro', 'Who closes the work when it is done. These are company defaults — a work type can need review even when the company does not.')}
                </p>
              </div>
              <div className="mt-2 divide-y divide-border border-t border-border">
                {completionToggles.map((row) => (
                  <div key={row.key} className="flex items-start justify-between gap-4 p-4">
                    <div className="min-w-0">
                      <Label htmlFor={`pol-${row.key}`} className="text-sm font-medium text-charcoal">{row.label}</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">{row.help}</p>
                    </div>
                    <Switch id={`pol-${row.key}`} checked={!!draft[row.key]} disabled={saving}
                      onCheckedChange={(v) => set(row.key, v)} aria-label={row.label} />
                  </div>
                ))}
              </div>
            </section>

            <AssignmentGroupsSection disabled={saving} />

            <div className="flex items-start gap-2 rounded-[10px] bg-muted px-3 py-2 text-xs text-muted-foreground">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{k('note', 'These settings control who may assign and to whom. They never widen who can SEE work — visibility follows management scope and each person’s own items. Custom roles such as a Work Coordinator are created under Roles & permissions.')}</span>
              <span className="block">{k('note_workload', 'Whose workload a manager may see is set under Workload & Health.')}</span>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" disabled={!dirty || saving} onClick={() => base && setDraft(base)}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button disabled={!dirty || saving} onClick={submit}>
                {saving ? k('saving', 'Saving…') : t('common.save', { defaultValue: 'Save' })}
              </Button>
            </div>
          </>
        )}
      </div>
    </RequirePermission>
  );
};

export default WorkAccessPage;
